import ReportSectionCard from './shared/ReportSectionCard.component'
import ReportSectionHeading from '@components/pages/Reports/shared/ReportSectionHeading.component'
import React, { useMemo, useState } from 'react'
import ReportViewTabs from '@components/pages/Reports/shared/ReportViewTabs.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import ReportBundleStickyTopWhenExpanded from '@components/pages/Reports/shared/ReportBundleStickyTopWhenExpanded.component'
import ReportFilterToolbar from '@components/pages/Reports/shared/ReportFilterToolbar.component'
import ReportChartBodySkeleton from '@components/pages/Reports/shared/ReportChartBodySkeleton.component'
import ReportKpiGrid from '@components/pages/Reports/shared/ReportKpiGrid.component'
import ReportPageHeader from '@components/pages/Reports/shared/ReportPageHeader.component'
import { REPORTS_DISTRIBUTION_VIEW_TABS } from '@constants/Reports/patientBaseHealth.constants'
import {
  HYGIENE_PERFORMANCE_FILTERS,
  HYGIENE_PERFORMANCE_KPI_DEFINITIONS,
} from '@constants/Reports/hygienePerformance.constants'
import {
  ReportsDistributionView,
  ReportsKpiMetric,
} from '@dataTypes/reports.type'
import { useHygienePerformanceReport } from '@hooks/reports/useHygienePerformanceReport'
import { useReportFinanceUiEffects } from '@hooks/reports/useReportFinanceUiEffects'
import {
  hasNonZeroDashboardValues,
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import {
  computeDynamicCountAxis,
  computePercentAxis,
  formatKpiPercent,
} from '@utils/reports/revenueRealizationFinance.util'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from '@components/pages/Reports/shared/ReportDesignSystemCharts.component'

const REPORT_CHART_AXIS_TICK = { fontSize: 12, fill: 'var(--gray-500)' }
const REPORT_CHART_TOOLTIP_STYLE = {
  borderRadius: 8,
  border: '1px solid var(--gray-200)',
  fontSize: 12,
}
const REPORT_CHART_GRID_STROKE = 'var(--gray-100)'
const REPORT_BAR_CHART_MARGIN = { top: 24, right: 12, left: 4, bottom: 0 }
const REPORT_BAR_CATEGORY_GAP = '16%'
const REPORT_YAXIS_WIDTH = 46

function formatKpiNumber(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return '—'
  return Math.round(value).toLocaleString('en-US')
}

function HygienePerformanceReport(): React.ReactElement {
  const [openingsView, setOpeningsView] =
    useState<ReportsDistributionView>('Count')
  const [reportSaveViewAction, setReportSaveViewAction] =
    useState<JSX.Element | null>(null)
  const [reportGeneratePDFAction, setReportGeneratePDFAction] =
    useState<JSX.Element | null>(null)
  // `reportFiltersAndMetrics` is the source of truth for the filters and metrics of the currently selected report view.
  // Use this for all data queries and any logic that depends on the selected report view.
  const [reportFiltersAndMetrics, setReportFiltersAndMetrics] = useState<{
    filters: Record<string, boolean>
    metrics: Record<string, boolean>
  } | null>(null)
  const { loading, error, data } = useHygienePerformanceReport(
    (reportFiltersAndMetrics?.filters ?? {}) as unknown as Record<
      string,
      string
    >,
  )
  useReportFinanceUiEffects({
    loading,
    error,
    errorMessage: 'Failed to load hygiene performance data. Please try again.',
  })

  const kpiItems = useMemo((): ReportsKpiMetric[] => {
    if (!data) {
      return HYGIENE_PERFORMANCE_KPI_DEFINITIONS.map((def) => ({
        id: def.id,
        title: def.title,
        footer: def.footer,
        value: loading ? '…' : '—',
      }))
    }

    const valueById: Record<string, number> = {
      'hygiene-visits': data.hygieneVisits.value,
      'hygiene-openings-total': data.hygieneOpenings.value,
      'hygiene-utilization': data.hygieneUtilization.value,
      // TODO: unhide Recall Compliance and Hygiene Reactivation when ready for implementation
      // 'recall-compliance': data.recallCompliance.value,
      // 'hygiene-reactivation': data.hygieneReactivation.value,
    }

    return HYGIENE_PERFORMANCE_KPI_DEFINITIONS.map((def) => {
      const raw = valueById[def.id]
      let value: string
      if (def.format === 'percent') {
        value = formatKpiPercent(raw)
      } else {
        value = formatKpiNumber(raw)
      }
      return {
        id: def.id,
        title: def.title,
        footer: def.footer,
        value,
      }
    })
  }, [data, loading])

  const openingsTotal =
    data?.hygieneOpeningsByPeriod.buckets.reduce(
      (sum, row) => sum + row.value,
      0,
    ) ?? 0

  const openingsChartData = useMemo(() => {
    if (!data) return []
    const total = openingsTotal || 1
    return data.hygieneOpeningsByPeriod.buckets.map((row) => {
      if (openingsView === 'Count') {
        return {
          month: row.label,
          barValue: row.value,
          topLabel: row.value.toFixed(1),
        }
      }
      const pct = Math.round((row.value / total) * 1000) / 10
      return {
        month: row.label,
        barValue: pct,
        topLabel: `${pct}%`,
      }
    })
  }, [data, openingsView, openingsTotal])

  const openingsYAxis = useMemo(() => {
    if (openingsView === 'Percentage') {
      return computePercentAxis(openingsChartData.map((row) => row.barValue))
    }
    return computeDynamicCountAxis(openingsChartData.map((row) => row.barValue))
  }, [openingsView, openingsChartData])

  const utilizationChartData = useMemo(() => {
    if (!data) return []
    return data.hygieneUtilizationByPeriod.buckets.map((row) => ({
      month: row.label,
      barValue: row.value,
      topLabel: `${row.value}%`,
    }))
  }, [data])

  const utilizationYAxis = useMemo(
    () => computePercentAxis(utilizationChartData.map((row) => row.barValue)),
    [utilizationChartData],
  )

  const chartsPending = isDashboardSectionPending(loading, Boolean(data))
  const kpiEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      data?.hygieneVisits.value,
      data?.hygieneOpenings.value,
      data?.hygieneUtilization.value,
    ),
  )
  const openingsEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.hygieneOpeningsByPeriod?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const utilizationEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.hygieneUtilizationByPeriod?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )

  // TODO: unhide Recall Compliance and Hygiene Reactivation when ready for implementation
  // const recallChartData = useMemo(() => {
  //   if (!data) return []
  //   return data.recallComplianceByPeriod.buckets.map((row) => ({
  //     month: row.label,
  //     barValue: row.value,
  //     topLabel: `${row.value}%`,
  //   }))
  // }, [data])

  // const recallYAxis = useMemo(
  //   () => computePercentAxis(recallChartData.map((row) => row.barValue)),
  //   [recallChartData],
  // )

  // const reactivationChartData = useMemo(() => {
  //   if (!data) return []
  //   return data.hygieneReactivationByPeriod.buckets.map((row) => ({
  //     month: row.label,
  //     barValue: row.value,
  //     topLabel: `${row.value}%`,
  //   }))
  // }, [data])

  // const reactivationYAxis = useMemo(
  //   () => computePercentAxis(reactivationChartData.map((row) => row.barValue)),
  //   [reactivationChartData],
  // )

  return (
    <div className="reports-patient-base-health reports-treatment-conversion-funnel reports-hygiene-performance">
      <ReportBundleStickyTopWhenExpanded>
        <ReportPageHeader title="Hygiene Performance">
          {reportSaveViewAction}
          {reportGeneratePDFAction}
        </ReportPageHeader>

        <ReportFilterToolbar
          filters={HYGIENE_PERFORMANCE_FILTERS}
          onReportSaveViewActionChange={setReportSaveViewAction}
          onReportGeneratePDFActionChange={setReportGeneratePDFAction}
          onReportFiltersAndMetricsChange={setReportFiltersAndMetrics}
        />
      </ReportBundleStickyTopWhenExpanded>

      {kpiEmpty ? (
        <DashboardSectionEmptyState />
      ) : (
        <ReportKpiGrid items={kpiItems} loading={chartsPending} />
      )}

      <div className="reports-treatment-conversion-funnel__stack">
        <div className="reports-treatment-conversion-funnel__grid">
          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Hygiene Openings (Avg per Day)
                </ReportSectionHeading>
                {chartsPending || openingsEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={openingsView}
                    onChange={(value) =>
                      setOpeningsView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {chartsPending ? (
                <ReportChartBodySkeleton />
              ) : openingsEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={openingsChartData}
                      margin={REPORT_BAR_CHART_MARGIN}
                      barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        domain={openingsYAxis.domain}
                        ticks={openingsYAxis.ticks}
                        tickFormatter={openingsYAxis.tickFormatter}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip
                        formatter={(value: number) =>
                          openingsView === 'Count' ? value : `${value}%`
                        }
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Bar
                        dataKey="barValue"
                        fill="var(--blue-400)"
                        radius={[8, 8, 8, 8]}
                        barSize={100}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </ReportSectionCard>

          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>Hygiene Utilization</ReportSectionHeading>
              </div>
              {chartsPending ? (
                <ReportChartBodySkeleton />
              ) : utilizationEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={utilizationChartData}
                      margin={REPORT_BAR_CHART_MARGIN}
                      barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        domain={utilizationYAxis.domain}
                        ticks={utilizationYAxis.ticks}
                        tickFormatter={utilizationYAxis.tickFormatter}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip
                        formatter={(value: number) => `${value}%`}
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Bar
                        dataKey="barValue"
                        fill="var(--blue-400)"
                        radius={[8, 8, 8, 8]}
                        barSize={100}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </ReportSectionCard>
        </div>

        {/* TODO: unhide Recall Compliance and Hygiene Reactivation when ready for implementation
        <div className="reports-treatment-conversion-funnel__grid">
          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>Recall Compliance</ReportSectionHeading>
              </div>
              <div className="reports-treatment-conversion-funnel__chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={recallChartData}
                    margin={REPORT_BAR_CHART_MARGIN}
                    barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                  >
                    <CartesianGrid
                      vertical={false}
                      strokeDasharray="0"
                      stroke={REPORT_CHART_GRID_STROKE}
                    />
                    <XAxis
                      dataKey="month"
                      axisLine={false}
                      tickLine={false}
                      tick={REPORT_CHART_AXIS_TICK}
                    />
                    <YAxis
                      domain={recallYAxis.domain}
                      ticks={recallYAxis.ticks}
                      tickFormatter={recallYAxis.tickFormatter}
                      axisLine={false}
                      tickLine={false}
                      tick={REPORT_CHART_AXIS_TICK}
                      width={REPORT_YAXIS_WIDTH}
                    />
                    <Tooltip
                      formatter={(value: number) => `${value}%`}
                      contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                    />
                    <Bar
                      dataKey="barValue"
                      fill="var(--blue-400)"
                      radius={[8, 8, 8, 8]}
                      barSize={100}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </ReportSectionCard>

          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>Hygiene Reactivation Success</ReportSectionHeading>
              </div>
              <div className="reports-treatment-conversion-funnel__chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={reactivationChartData}
                    margin={REPORT_BAR_CHART_MARGIN}
                    barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                  >
                    <CartesianGrid
                      vertical={false}
                      strokeDasharray="0"
                      stroke={REPORT_CHART_GRID_STROKE}
                    />
                    <XAxis
                      dataKey="month"
                      axisLine={false}
                      tickLine={false}
                      tick={REPORT_CHART_AXIS_TICK}
                    />
                    <YAxis
                      domain={reactivationYAxis.domain}
                      ticks={reactivationYAxis.ticks}
                      tickFormatter={reactivationYAxis.tickFormatter}
                      axisLine={false}
                      tickLine={false}
                      tick={REPORT_CHART_AXIS_TICK}
                      width={REPORT_YAXIS_WIDTH}
                    />
                    <Tooltip
                      formatter={(value: number) => `${value}%`}
                      contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                    />
                    <Bar
                      dataKey="barValue"
                      fill="var(--blue-400)"
                      radius={[8, 8, 8, 8]}
                      barSize={100}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </ReportSectionCard>
        </div>
        */}
      </div>
    </div>
  )
}

export default HygienePerformanceReport
